import { getApiClient } from '@aslanpay/portal-common-lib';

import {
  AcceptReconciliationFindingRequest,
  FetchReconciliationsRequest,
  ListPage,
  QueueReconciliationRequest,
  ReconciliationCompaniesRequest,
  ReconciliationCompany,
  ReconciliationFinding,
  ReconciliationFindingsRequest,
  ReconciliationReport,
  ReconciliationReportRequest,
  ReconciliationRun,
  ResolveReconciliationFindingRequest,
  UnverifiableAggregate,
  UnverifiableAggregatesRequest,
} from 'app/types/reconciliations';

const BASE_PATH = '/reports-service/reconciliations';
const FINDINGS_PATH = '/reports-service/findings';
const DEFAULT_REPORT_FILENAME = 'Aslan_Reconciliation_Report.csv';

interface ReconciliationRunsResponse {
  list: ReconciliationRun[];
  pageNumber: number;
  pageSize: number;
  total: number;
}

/**
 * @description Reads the file name the backend gives a download, falling back to a default.
 */
export const getFilename = (contentDisposition?: string) => {
  const encoded = contentDisposition?.match(/filename\*=UTF-8''([^";]+)/)?.[1];
  if (encoded) {
    return decodeURIComponent(encoded);
  }

  return contentDisposition?.match(/filename="?([^";]+)"?/)?.[1] || DEFAULT_REPORT_FILENAME;
};

const ReconciliationsApiHandler = {
  listReconciliations: async ({ companyId, status, page, size }: FetchReconciliationsRequest) => {
    const { data } = await getApiClient().get<ReconciliationRunsResponse>(BASE_PATH, {
      params: { companyId, status, page, size },
      paramsSerializer: { indexes: null },
    });
    return data;
  },

  getReconciliation: async (id: string) => {
    const { data } = await getApiClient().get<ReconciliationRun>(`${BASE_PATH}/${id}`);
    return data;
  },

  queueReconciliation: async (body: QueueReconciliationRequest) => {
    const { data } = await getApiClient().post<ReconciliationRun>(BASE_PATH, body);
    return data;
  },

  getReconciliationReport: async ({ id, companyId }: ReconciliationReportRequest) => {
    const { data } = await getApiClient().get<ReconciliationReport>(`${BASE_PATH}/${id}/report`, {
      params: { companyId },
    });
    return data;
  },

  listReconciliationCompanies: async ({
    id,
    companyId,
    status,
    page,
    size,
  }: ReconciliationCompaniesRequest) => {
    const { data } = await getApiClient().get<ListPage<ReconciliationCompany>>(
      `${BASE_PATH}/${id}/companies`,
      { params: { companyId, status, page, size } }
    );
    return data;
  },

  listUnverifiableAggregates: async ({
    id,
    companyId,
    page,
    size,
  }: UnverifiableAggregatesRequest) => {
    const { data } = await getApiClient().get<ListPage<UnverifiableAggregate>>(
      `${BASE_PATH}/${id}/unverifiable-aggregates`,
      { params: { companyId, page, size } }
    );
    return data;
  },

  listFindings: async ({
    ids,
    firstSeenReconciliationId,
    resolvedReconciliationId,
    lapsed,
    manuallyResolved,
    accepted,
    companyIds,
    page,
    size,
  }: ReconciliationFindingsRequest) => {
    const { data } = await getApiClient().get<ListPage<ReconciliationFinding>>(FINDINGS_PATH, {
      params: {
        ids,
        firstSeenReconciliationId,
        resolvedReconciliationId,
        lapsed,
        manuallyResolved,
        accepted,
        companyIds,
        page,
        size,
      },
      paramsSerializer: { indexes: null },
    });
    return data;
  },

  resolveFinding: async ({ id, note }: ResolveReconciliationFindingRequest) => {
    const { data } = await getApiClient().post<ReconciliationFinding>(
      `${FINDINGS_PATH}/${id}/resolve`,
      {
        note,
      }
    );
    return data;
  },

  acceptFinding: async ({ id, note }: AcceptReconciliationFindingRequest) => {
    const { data } = await getApiClient().post<ReconciliationFinding>(
      `${FINDINGS_PATH}/${id}/accept`,
      {
        note,
      }
    );
    return data;
  },

  downloadReconciliationReport: async ({ id, companyId }: ReconciliationReportRequest) => {
    const { data, headers } = await getApiClient().get<Blob>(`${BASE_PATH}/${id}/report/download`, {
      params: { companyId },
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = getFilename(headers['content-disposition'] as string | undefined);
    link.click();
    setTimeout(() => window.URL.revokeObjectURL(url), 0);
  },
};

export default ReconciliationsApiHandler;
